import { GraphUp, Group, UserPlus } from 'iconoir-react';
import { NAV_ICON_SIZE, NAV_ICON_STROKE_WIDTH } from '@/components/icons';
import { SkeletonText } from '@/components/ui/skeleton';
import { useProgramQuery } from '@/hooks/queries/use-affiliate-program-query';
import useNavBarStore, { resolvePanelForPath } from '@/stores/nav-store';
import { Box, Flex, VStack } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router-dom';
import NavLink from '../../nav-bar/nav-link';
import SidebarPanelHeader from '../SidebarPanelHeader';

interface ProgramMenuProps {
  onBack: () => void;
}

const ProgramMenu: React.FC<ProgramMenuProps> = ({ onBack }) => {
  const { t } = useTranslation();
  // The panel is rendered by the Layout's sidebar, outside the /programs/:id
  // route element, so useParams cannot see :id here — derive it from the
  // pathname instead (falling back to the seeded panel data during the exit
  // animation, when the pathname has already left the program section).
  const location = useLocation();

  const { currentData: navBarProgramInfo, setData } = useNavBarStore();

  const routeProgramId =
    resolvePanelForPath(location.pathname) === 'program'
      ? matchPath('/programs/:id/*', location.pathname)?.params.id
      : undefined;
  const programId = routeProgramId ?? navBarProgramInfo?.id;

  const {
    data: programFromHook,
    isLoading,
    error,
  } = useProgramQuery({
    programId: programId,
    enabled: !!programId && !(navBarProgramInfo?.id === programId && navBarProgramInfo?.name),
  });

  useEffect(() => {
    if (programFromHook) {
      if (
        programFromHook.id !== navBarProgramInfo?.id ||
        programFromHook.name !== navBarProgramInfo?.name
      ) {
        setData({ id: programFromHook.id, name: programFromHook.name });
      }
    }
  }, [programFromHook, navBarProgramInfo, setData]);

  const getDisplayState = () => {
    if (programFromHook?.name) {
      return { name: programFromHook.name, showSkeleton: false };
    }
    if (navBarProgramInfo?.id === programId && navBarProgramInfo?.name) {
      return { name: navBarProgramInfo.name, showSkeleton: false };
    }
    if (isLoading) {
      return { name: t('common.loading'), showSkeleton: true };
    }
    if (error) {
      return { name: t('common.error'), showSkeleton: false };
    }
    if (!programId) {
      return { name: t('programs.selectProgram'), showSkeleton: false };
    }
    return { name: t('programs.details'), showSkeleton: false };
  };

  const { name: displayProgramName, showSkeleton } = getDisplayState();

  const headerTitle = showSkeleton ? (
    <SkeletonText noOfLines={1} w="160px" h={6} />
  ) : (
    displayProgramName
  );

  const links = programId
    ? [
        {
          to: `/programs/${programId}/overview`,
          label: t('programs.overview'),
          icon: (
            <GraphUp
              width={NAV_ICON_SIZE}
              height={NAV_ICON_SIZE}
              strokeWidth={NAV_ICON_STROKE_WIDTH}
            />
          ),
        },
        {
          to: `/programs/${programId}/affiliates`,
          label: t('programs.affiliates'),
          icon: (
            <Group
              width={NAV_ICON_SIZE}
              height={NAV_ICON_SIZE}
              strokeWidth={NAV_ICON_STROKE_WIDTH}
            />
          ),
        },
        {
          to: `/programs/${programId}/invitation`,
          label: t('programs.invitations'),
          icon: (
            <UserPlus
              width={NAV_ICON_SIZE}
              height={NAV_ICON_SIZE}
              strokeWidth={NAV_ICON_STROKE_WIDTH}
            />
          ),
        },
      ]
    : [];

  return (
    <Flex direction="column" h="full" overflow="hidden" gapY={4} px={3} pt={4} pb="14px">
      <Box>
        <SidebarPanelHeader title={headerTitle} onBack={onBack} />
      </Box>
      <Box flex={1}>
        <VStack align="stretch" gap="1px">
          {links.map((link) => (
            <NavLink key={link.to} label={link.label} to={link.to} icon={link.icon} />
          ))}
        </VStack>
      </Box>
    </Flex>
  );
};

export default ProgramMenu;
